import t from "@/lang/lang";
import {
  usePestcontrolMonitorDeleteMutation,
  usePestcontrolMonitorListQuery,
  usePestcontrolMonitorPutMutation,
} from "@/services/backend/pestcontrol/api/monitor-api";
import {
  PestcontrolMonitor,
  PestcontrolMonitorListRequest,
  PestcontrolMonitorOrderBy,
  PestcontrolMonitorType,
} from "@/services/backend/pestcontrol/types/monitor";
import {
  formatDuration,
  formatMonitorType,
  formatPrice,
  newMonitorDraft,
  normalizeKey,
  normalizeMonitor,
  normalizeMonitorForSave,
} from "@/services/backend/pestcontrol/lib/monitor";
import { PestcontrolField } from "@/services/backend/pestcontrol/components/pestcontrol-field";
import { PestcontrolRowActions } from "@/services/backend/pestcontrol/components/row-actions";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  DataTable,
  DataTableBrowserPagination,
  DataTableContainer,
  DataTableFilters,
  useDataTableController,
} from "@/shared/components/tables/data-table";
import { OrderBySelect } from "@/shared/components/tables/order-by-select";
import { SearchFilter } from "@/shared/components/filter/search-filter";
import { Button } from "@/shared/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/shared/components/ui/dialog";
import { Input } from "@/shared/components/ui/input";
import { Label } from "@/shared/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/shared/components/ui/select";
import { Checkbox } from "@/shared/components/ui/checkbox";
import { TimeNumberInput } from "@/shared/components/inputs/time-number-input";
import { FilterMode } from "@/shared/lib/filter/filter";
import { ColumnDef } from "@tanstack/react-table";
import { Plus } from "lucide-react";
import { type ReactNode, useEffect, useMemo, useRef, useState } from "react";

export function PestcontrolMonitorTable({
  companyId,
  request,
  onRequestChange,
  onEditMonitor,
}: {
  companyId: string;
  request: PestcontrolMonitorListRequest;
  onRequestChange: (request: PestcontrolMonitorListRequest) => void;
  onEditMonitor: (monitorId: string) => void;
}) {
  const { data, isLoading, error } = usePestcontrolMonitorListQuery(request);
  const [deleteMonitor, deleteMonitorState] =
    usePestcontrolMonitorDeleteMutation();
  const monitors = useMemo(
    () => (data?.items ?? []).map(normalizeMonitor),
    [data?.items],
  );
  const columns = useMonitorColumns({
    onEdit: (monitor) => onEditMonitor(monitor.id),
    onDelete: (monitor) => deleteMonitor({ id: monitor.id }).unwrap(),
  });
  const table = useDataTableController(columns, monitors);

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  return (
    <DataTableContainer className={isLoading ? "animate-pulse" : undefined}>
      <DataTableFilters className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <SearchFilter
            className="h-9 w-[230px]"
            filter={
              request.name ?? {
                active: false,
                mode: FilterMode.Like,
                values: [],
              }
            }
            onFilterChange={(name) => onRequestChange({ ...request, name })}
            placeholder={t("Nach Name suchen ...")}
          />
          <MonitorTypeFilter
            value={request.type?.values?.[0] ?? "all"}
            onChange={(type) =>
              onRequestChange({
                ...request,
                type:
                  type === "all"
                    ? { active: false, values: [] }
                    : { active: true, values: [type] },
              })
            }
          />
          <OrderBySelect<PestcontrolMonitorOrderBy>
            orderBy={request.orderBy}
            onOrderByChange={(orderBy) =>
              onRequestChange({ ...request, orderBy })
            }
            fields={[
              { label: t("Name"), value: "name" },
              { label: t("Typ"), value: "type" },
              { label: t("Lieferant"), value: "supplier" },
              { label: t("Erstellt am"), value: "createdAt" },
              { label: t("Aktualisiert am"), value: "updatedAt" },
            ]}
          />
        </div>
        <CreateMonitorDialog
          companyId={companyId}
          onCreated={(monitor) => onEditMonitor(monitor.id)}
          trigger={
            <Button>
              <Plus className="size-4" />
              {t("Monitor anlegen")}
            </Button>
          }
        />
      </DataTableFilters>
      <DataTable columns={columns} table={table} />
      <DataTableBrowserPagination table={table} />
      {deleteMonitorState.error && (
        <RTKQueryErrorAlert error={deleteMonitorState.error} />
      )}
    </DataTableContainer>
  );
}

function MonitorTypeFilter({
  value,
  onChange,
}: {
  value: PestcontrolMonitorType | "all";
  onChange: (value: PestcontrolMonitorType | "all") => void;
}) {
  return (
    <Select
      value={value}
      onValueChange={(next) =>
        onChange(next === "all" ? "all" : (next as PestcontrolMonitorType))
      }
    >
      <SelectTrigger className="h-9 w-[190px]">
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all">{t("Alle Typen")}</SelectItem>
        {Object.values(PestcontrolMonitorType).map((type) => (
          <SelectItem key={type} value={type}>
            {formatMonitorType(type)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
}

function CreateMonitorDialog({
  companyId,
  trigger,
  onCreated,
}: {
  companyId: string;
  trigger?: ReactNode;
  onCreated: (monitor: PestcontrolMonitor) => void;
}) {
  const [putMonitor, putState] = usePestcontrolMonitorPutMutation();
  const [draft, setDraft] = useState<PestcontrolMonitor>(
    newMonitorDraft(companyId),
  );
  const [internalOpen, setInternalOpen] = useState(false);
  const isOpen = internalOpen;
  const setOpen = setInternalOpen;
  const canSave = draft.name.trim().length > 0 && !putState.isLoading;
  const resetPutState = putState.reset;
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen && !wasOpenRef.current) {
      resetPutState();
    }
    wasOpenRef.current = isOpen;
  }, [isOpen, resetPutState]);

  const save = async () => {
    if (!canSave) return;
    const key = normalizeKey(draft.key || draft.name);
    let monitor: PestcontrolMonitor;
    try {
      monitor = await putMonitor({
        companyId,
        monitor: normalizeMonitorForSave({
          ...draft,
          companyId,
          key,
          name: draft.name.trim(),
        }),
        pestIds: [],
        groupIds: [],
      }).unwrap();
    } catch {
      return;
    }
    setOpen(false);
    setDraft(newMonitorDraft(companyId));
    onCreated(monitor);
  };

  return (
    <Dialog open={isOpen} onOpenChange={setOpen}>
      {trigger && <DialogTrigger asChild>{trigger}</DialogTrigger>}
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t("Monitor anlegen")}</DialogTitle>
          <DialogDescription>
            {t("Erstellen Sie hier einen neuen Basis-Monitor.")}
          </DialogDescription>
        </DialogHeader>
        <div className="grid gap-4">
          <PestcontrolField label={t("Name")}>
            <Input
              value={draft.name}
              onChange={(event) =>
                setDraft({
                  ...draft,
                  name: event.target.value,
                  key: normalizeKey(event.target.value),
                })
              }
            />
          </PestcontrolField>
          <PestcontrolField label={t("Typ")}>
            <Select
              value={draft.type}
              onValueChange={(value) =>
                setDraft({ ...draft, type: value as PestcontrolMonitorType })
              }
            >
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.values(PestcontrolMonitorType).map((type) => (
                  <SelectItem key={type} value={type}>
                    {formatMonitorType(type)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </PestcontrolField>
          <div className="flex items-center gap-2">
            <Checkbox
              id="new-checkpoint-visual"
              checked={draft.capabilities.checkpointVisual}
              onCheckedChange={(checked) =>
                setDraft({
                  ...draft,
                  capabilities: {
                    ...draft.capabilities,
                    checkpointVisual: checked === true,
                  },
                })
              }
            />
            <Label htmlFor="new-checkpoint-visual">
              {t("Visueller Prüfpunkt")}
            </Label>
          </div>
          <PestcontrolField label={t("Installationszeit")}>
            <TimeNumberInput
              value={draft.times.install}
              onChange={(install) =>
                setDraft({
                  ...draft,
                  times: { ...draft.times, install },
                })
              }
            />
          </PestcontrolField>
          <PestcontrolField label={t("Servicezeit")}>
            <TimeNumberInput
              value={draft.times.maintain}
              onChange={(maintain) =>
                setDraft({
                  ...draft,
                  times: { ...draft.times, maintain },
                })
              }
            />
          </PestcontrolField>
        </div>
        {putState.error && <RTKQueryErrorAlert error={putState.error} />}
        <DialogFooter>
          <Button
            type="button"
            variant="outline"
            onClick={() => setOpen(false)}
          >
            {t("Schließen")}
          </Button>
          <Button type="button" onClick={save} disabled={!canSave}>
            {t("Erstellen")}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function useMonitorColumns({
  onEdit,
  onDelete,
}: {
  onEdit: (monitor: PestcontrolMonitor) => void;
  onDelete: (monitor: PestcontrolMonitor) => Promise<unknown>;
}): ColumnDef<PestcontrolMonitor>[] {
  return useMemo(
    () => [
      {
        header: t("Name"),
        accessorKey: "name",
      },
      {
        header: t("Typ"),
        cell: ({ row }) => formatMonitorType(row.original.type),
      },
      {
        header: t("Lieferant"),
        cell: ({ row }) => row.original.supplier || "-",
      },
      {
        header: t("Montage"),
        cell: ({ row }) => formatDuration(row.original.times.install),
      },
      {
        header: t("Service"),
        cell: ({ row }) => formatDuration(row.original.times.maintain),
      },
      {
        header: t("Einkauf"),
        cell: ({ row }) => formatPrice(row.original.buyPrice),
      },
      {
        header: t("Empfohlene Maßnahmen"),
        accessorKey: "recommendedVisits",
      },
      {
        header: t("Schädlinge"),
        cell: ({ row }) => row.original.pests?.length || "-",
      },
      {
        header: t("Gruppen"),
        cell: ({ row }) => row.original.groups?.length || "-",
      },
      {
        id: "actions",
        header: t("Aktion"),
        cell: ({ row }) => (
          <PestcontrolRowActions
            onEdit={() => onEdit(row.original)}
            onDelete={() => onDelete(row.original)}
          />
        ),
      },
    ],
    [onDelete, onEdit],
  );
}
